/**
 * ShareModal - Product sharing component
 * Supports Web Share API, WhatsApp, Facebook, Instagram, X, Email, Copy Link
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Share2, Check, Mail, MessageCircle, Link2, Smartphone, Instagram } from "lucide-react";

export const ShareModal = ({ isOpen, onClose, product, url = null }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = url || window.location.href;
  const shareTitle = product?.name || product?.title || "Check this out on Liantiao";
  const shareText = product?.description 
    ? product.description.substring(0, 100) + "..."
    : "Found this on Liantiao Jamaica!";
  const shareImage = product?.images?.[0] || product?.image;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        trackShare("native");
        onClose();
      } catch (err) {
        if (err.name !== "AbortError") console.error("Share failed:", err);
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      trackShare("copy");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(shareTitle + " - " + shareUrl);
    window.open("https://wa.me/?text=" + text, "_blank");
    trackShare("whatsapp");
  };

  const handleFacebook = () => {
    const fbUrl = encodeURIComponent(shareUrl);
    window.open("https://www.facebook.com/sharer/sharer.php?u=" + fbUrl, "_blank", "width=600,height=400");
    trackShare("facebook");
  };

  const handleInstagram = () => {
    // Instagram doesn't have a direct web share API, so we copy the link and guide the user
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success("Link copied! Open Instagram and paste in your story or DM", { duration: 4000 });
      trackShare("instagram");
      // Open Instagram app/website
      window.open("https://www.instagram.com/", "_blank");
    }).catch(() => {
      toast.info("Copy the link and share on Instagram");
    });
  };

  const handleTwitter = () => {
    const text = encodeURIComponent(shareTitle);
    const tUrl = encodeURIComponent(shareUrl);
    window.open("https://twitter.com/intent/tweet?text=" + text + "&url=" + tUrl, "_blank", "width=600,height=400");
    trackShare("twitter");
  };

  const handleEmail = () => {
    const subject = encodeURIComponent("Check this on Liantiao: " + shareTitle);
    const body = encodeURIComponent(shareText + "\n\n" + shareUrl);
    window.location.href = "mailto:?subject=" + subject + "&body=" + body;
    trackShare("email");
  };

  const trackShare = async (channel) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || "";
      await fetch(API_URL + "/api/metrics/share", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product?.id, channel, timestamp: new Date().toISOString() })
      });
    } catch (e) { /* Silent fail */ }
  };

  const canNativeShare = typeof navigator !== "undefined" && navigator.share;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-primary" />
            Share Product
          </DialogTitle>
        </DialogHeader>
        
        {product && (
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg mb-4">
            {shareImage && (
              <img src={shareImage} alt={shareTitle} className="w-16 h-16 object-cover rounded-lg" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{shareTitle}</p>
              {product.price && (
                <p className="text-primary font-semibold">
                  J${typeof product.price === "number" ? product.price.toLocaleString() : product.price}
                </p>
              )}
            </div>
          </div>
        )}

        {canNativeShare && (
          <Button onClick={handleNativeShare} className="w-full mb-4 bg-primary hover:bg-primary/90" data-testid="native-share-btn">
            <Smartphone className="w-4 h-4 mr-2" />Share via Device
          </Button>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={handleCopyLink} className="flex items-center justify-center gap-2" data-testid="copy-link-btn">
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
            {copied ? "Copied!" : "Copy Link"}
          </Button>
          <Button variant="outline" onClick={handleWhatsApp} className="flex items-center justify-center gap-2 hover:bg-green-500/10 hover:border-green-500" data-testid="whatsapp-share-btn">
            <MessageCircle className="w-4 h-4 text-green-500" />WhatsApp
          </Button>
          <Button variant="outline" onClick={handleFacebook} className="flex items-center justify-center gap-2 hover:bg-blue-500/10 hover:border-blue-500" data-testid="facebook-share-btn">
            <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            Facebook
          </Button>
          <Button variant="outline" onClick={handleInstagram} className="flex items-center justify-center gap-2 hover:bg-pink-500/10 hover:border-pink-500" data-testid="instagram-share-btn">
            <Instagram className="w-4 h-4 text-pink-500" />
            Instagram
          </Button>
          <Button variant="outline" onClick={handleTwitter} className="flex items-center justify-center gap-2 hover:bg-slate-500/10 hover:border-slate-500" data-testid="twitter-share-btn">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            X (Twitter)
          </Button>
          <Button variant="outline" onClick={handleEmail} className="flex items-center justify-center gap-2 hover:bg-amber-500/10 hover:border-amber-500" data-testid="email-share-btn">
            <Mail className="w-4 h-4 text-amber-500" />Email
          </Button>
        </div>

        <div className="mt-4 p-2 bg-muted/30 rounded text-xs text-muted-foreground truncate">{shareUrl}</div>
      </DialogContent>
    </Dialog>
  );
};

export const ShareButton = ({ product, variant = "outline", size = "icon", className = "" }) => {
  const [showModal, setShowModal] = useState(false);
  
  return (
    <>
      <Button variant={variant} size={size} onClick={() => setShowModal(true)} className={className} data-testid="share-product-btn" title="Share this product">
        <Share2 className="w-4 h-4" />
      </Button>
      <ShareModal isOpen={showModal} onClose={() => setShowModal(false)} product={product} />
    </>
  );
};

export default ShareModal;
